import { FC } from 'react'

import { colors } from '@factorialco/deprecated-design-system'
import {
  BankingCardPaymentFilter,
  BankingEntitiesCardPaymentCalculatedLabelEnum,
  CardPaymentsWidgetDocument,
  CardPaymentsWidgetQuery,
} from 'generated/resources/graphql'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'

import { statusText } from 'modules/banking/lib/cardPayments/statusText'
import {
  ChartData,
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import {
  MonthsIntervalChartDataMap,
  cleanNotFilteredMonths,
  generateMonthsIntervalForChartDataMap,
  removeNullBetween,
} from 'modules/finance/components/Widgets/common/utils'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface PreparedDataArgsType {
  amountFormatter: (value: number) => string
}
interface QueryArgsType {
  accountIds: number[]
  search: string
}

const chartStatusColor = (status: BankingEntitiesCardPaymentCalculatedLabelEnum): string => {
  switch (status) {
    case 'complete':
      return colors.lime200
    case 'decline':
      return colors.radicalRed400
    case 'refund':
      return colors.champagne200
    case 'reverse':
      return colors.champagne200
    case 'withhold':
      return colors.grey400
  }
}

const chartCardPaymentTypes = (data: CardPaymentsWidgetQuery) => {
  return [
    {
      key: 'complete',
      source: data?.banking?.completeCardPayments,
      color: chartStatusColor('complete'),
      label: statusText('complete'),
    },
    {
      key: 'decline',
      source: data?.banking?.declineCardPayments,
      color: chartStatusColor('decline'),
      label: statusText('decline'),
    },
    {
      key: 'refund',
      source: data?.banking?.refundCardPayments,
      color: chartStatusColor('refund'),
      label: statusText('refund'),
    },
    {
      key: 'reverse',
      source: data?.banking?.reverseCardPayments,
      color: chartStatusColor('reverse'),
      label: statusText('reverse'),
    },
    {
      key: 'withhold',
      source: data?.banking?.withholdCardPayments,
      color: chartStatusColor('withhold'),
      label: statusText('withhold'),
    },
  ]
}

const generateMonthlyChartData = (data: CardPaymentsWidgetQuery) => {
  const cardPaymentTypes = chartCardPaymentTypes(data)

  const allDates: Date[] = []
  const valuesMap: Record<string, { values: string[]; amounts: number[] }> = {}

  cardPaymentTypes.forEach(({ key, source }) => {
    const values = source?.aggregate?.terms?.externalCreatedAt?.values || []
    const amounts = source?.aggregate?.terms?.externalCreatedAt?.sum?.amountCents || []
    valuesMap[key] = { values, amounts }
    allDates.push(...values.map((date) => new Date(date)))
  })

  if (allDates.length === 0) return []

  const minDate = new Date(Math.min(...allDates.map((date) => date.getTime())))
  const maxDate = new Date(Math.max(...allDates.map((date) => date.getTime())))

  const valueKeys = cardPaymentTypes
    .filter(({ key }) => (valuesMap[key]?.values?.length ?? 0) > 0)
    .map(({ key }) => `${key}AmountCents`)

  const chartDataMap: MonthsIntervalChartDataMap = generateMonthsIntervalForChartDataMap(
    minDate,
    maxDate,
    valueKeys
  )

  cardPaymentTypes.forEach(({ key }) => {
    if (valuesMap[key]) {
      const { values, amounts } = valuesMap[key]
      values.forEach((date, index) => {
        const month = moment(date).format('MMM YY')
        if (chartDataMap[month] && chartDataMap[month].value.hasOwnProperty(`${key}AmountCents`)) {
          chartDataMap[month].value[`${key}AmountCents`] =
            (chartDataMap[month].value[`${key}AmountCents`] || 0) + (amounts[index] || 0)
        }
      })
    }
  })

  return Object.values(chartDataMap)
}

const prepareData = (data: CardPaymentsWidgetQuery, prepareDataArgs: PreparedDataArgsType) => {
  const cardPaymentTypes = chartCardPaymentTypes(data)

  const totalAmounts = cardPaymentTypes.reduce(
    (acc, { key, source }) => {
      const amount = source?.aggregate?.sum?.amountCents || 0
      acc[key] = amount
      acc.total = (acc.total || 0) + amount
      return acc
    },
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    { total: 0 } as Record<string, number>
  )

  const barLabels = cardPaymentTypes.reduce<Record<string, { label: string; color: string }>>(
    (labels, { key, color, label }) => {
      labels[`${key}AmountCents`] = { label, color }
      return labels
    },
    {}
  )

  const countersChartDatas: ChartData[] = [
    {
      label: i18n.t('finance.overview.card_payments.total_amount'),
      value: totalAmounts.total || 0,
      formattedValue: prepareDataArgs.amountFormatter(totalAmounts.total || 0),
    },
    ...cardPaymentTypes
      .filter(({ key }) => totalAmounts[key] !== 0)
      .map(({ key, label }) => ({
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        label: label,
        value: totalAmounts[key] || 0,
        formattedValue: prepareDataArgs.amountFormatter(totalAmounts[key] || 0),
      })),
  ]

  const chartDataMap = generateMonthlyChartData(data)
  return {
    countersChartDatas,
    groupedChartData: chartDataMap,
    barLabels,
    amountFormatter: prepareDataArgs.amountFormatter,
  }
}

export const buildCardPaymentsInsightWidget = ({
  queryArgs,
  prepareDataArgs,
  filters,
}: {
  queryArgs: QueryArgsType
  prepareDataArgs: PreparedDataArgsType
  filters: BankingCardPaymentFilter
}) => {
  const filteredCardPaymentsFilters = removeNullBetween(filters)

  const CardPaymentsInsightsWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { countersChartDatas, groupedChartData, barLabels, amountFormatter } = data

    const cleanGroupedChartData = cleanNotFilteredMonths(
      groupedChartData,
      (current, key) => ({
        date: current.format('YYYY-MM-DD'),
        label: key,
        value: {
          completeAmountCents: 0,
          declineAmountCents: 0,
          refundAmountCents: 0,
          reverseAmountCents: 0,
          withholdAmountCents: 0,
        },
      }),
      filters.externalCreatedAt
    )

    return (
      <FinanceWidget
        id="cardPaymentsInsights"
        title={i18n.t('finance.overview.card_payments.title_account')}
        tickFormatter={(value) => amountFormatter(Number(value))}
        type={SupportedCharts.BAR_CHART_STACKED}
        showChart
        countersChartDatas={countersChartDatas}
        chartDatas={cleanGroupedChartData}
        barChartConfig={{
          barLabels: barLabels,
        }}
      />
    )
  }

  return buildWidget({
    id: 'cardPayments',
    component: CardPaymentsInsightsWidget,
    document: CardPaymentsWidgetDocument,
    prepareData: (data: CardPaymentsWidgetQuery) => prepareData(data, prepareDataArgs),
    queryArgs: {
      ...queryArgs,
      cardPaymentsFilters: { ...filteredCardPaymentsFilters },
    },
  })
}
